import { useMemo } from 'react';
import {
  UseDiningDrawerFooterConfigParams,
  UseDiningDrawerFooterConfigResult,
} from '@msc-b2c/interfaces/interfaces';
import {
  ChooseWhenBookBannerVariant,
  DiningFlowEntryPoint,
  DiningReservationMode,
  DiningStepId,
} from '@msc-b2c/interfaces/enums';

/**
 * Centralizes footer CTA labels, enabled/disabled states, and processing behavior
 * so DiningMultistepDrawer can focus on orchestration and rendering.
 *
 * Compact decision table (entryPoint x step)
 *
 * Legend:
 * - F=footer owns primary CTA progression (enabled/disabled + processing)
 * - I=inline step CTA owns progression (footer primary disabled)
 * - S=secondary cancel CTA shown in footer
 * - P=processing source for footer primary (`isConfirming` or `isProcessing`)
 *
 * +-------------------+------------------------+------------------------------+
 * | entryPoint        | step                   | behavior                     |
 * +-------------------+------------------------+------------------------------+
 * | PRODUCT_CARD      | PAX_SELECTION          | I                            |
 * | PRODUCT_CARD      | DATE_TIME_SELECTION    | I                            |
 * | PRODUCT_CARD      | DATE_SELECTION         | F, requires date+time        |
 * | PRODUCT_CARD      | TIME_SELECTION         | F, requires time             |
 * | PRODUCT_CARD      | CHOOSE_WHEN_BOOK       | F, mode-driven gate          |
 * | PRODUCT_CARD      | RESERVATION_RECAP      | F, slot-completion gate      |
 * | CART              | DATE_SELECTION         | F, S, P=isConfirming         |
 * | CART              | DATE_TIME_SELECTION    | F, S, P=isConfirming         |
 * | CART              | CART_RESERVATION_RECAP | F(change gate), S, recap UX  |
 * | ALREADY_PURCHASED | DATE_TIME_SELECTION    | F, S, P=isConfirming         |
 * | ALREADY_PURCHASED | PAX_SELECTION_EDIT     | I, S                         |
 * | ALREADY_PURCHASED | CART_RESERVATION_RECAP | F(change gate), S, recap UX  |
 * +-------------------+------------------------+------------------------------+
 *
 * Notes for maintainers:
 * - The DATE/TIME "footer-driven" behavior is modeled by `isFooterDrivenDateTimeStep`
 *   and intentionally overrides generic per-step behavior.
 * - Label sources are prioritized differently for CART recap vs generic edit flows.
 * - CHOOSE_WHEN_BOOK has an explicit AC4 exception: generic error still allows primary CTA.
 */
export const useDiningDrawerFooterConfig = ({
  entryPoint,
  currentStep,
  isCartReservationRecapStep,
  isFooterDrivenDateTimeStep,
  isCartOrAlreadyPurchasedPaxEditStep,
  isPaxSelectionEditStep,
  hasPaxSelectionChanges,
  hadReservationsOnEntry,
  isConfirming,
  isProcessing,
  hasCartReservationRecapChanges,
  isFooterDrivenDateTimeApplyReady,
  selectedDate,
  selectedTime,
  maximumSlotsBookable,
  chooseWhenBookBannerVariant,
  reservationMode,
  closeDrawerCancelLabel,
  addToCartButtonLabel,
  addingToCartButtonLabel,
  addingToCartButtonIcon,
  // new centralized CTA props (flat mapping from dining.diningCTA)
  cartConfirmButtonLabel,
  cartEditButtonLabel,
  cartUpdatingLabel,
  cartUpdatingIcon,
  alreadyPurchasedConfirmButtonLabel,
  alreadyPurchasedEditButtonLabel,
  alreadyPurchasedUpdatingLabel,
  alreadyPurchasedUpdatingIcon,
  isAddReservationFlow = false,
}: UseDiningDrawerFooterConfigParams): UseDiningDrawerFooterConfigResult => {
  return useMemo(() => {
    let primaryLabel: string | undefined = undefined;
    let processingPrimaryLabel: string | undefined = undefined;
    let processingPrimaryIcon: string | undefined = undefined;

    if (entryPoint === DiningFlowEntryPoint.PRODUCT_CARD) {
      // PRODUCT_CARD continues to use add-to-cart labels as before.
      primaryLabel = addToCartButtonLabel;
      processingPrimaryLabel = addingToCartButtonLabel;
      processingPrimaryIcon = addingToCartButtonIcon;
    } else if (entryPoint === DiningFlowEntryPoint.CART) {
      if (isCartReservationRecapStep) {
        primaryLabel = cartConfirmButtonLabel;
        processingPrimaryLabel = cartUpdatingLabel;
        processingPrimaryIcon = cartUpdatingIcon;
      } else {
        primaryLabel = cartEditButtonLabel;
        processingPrimaryLabel = cartUpdatingLabel;
        processingPrimaryIcon = cartUpdatingIcon;
      }
    } else if (entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED) {
      const isRecapOrConfirm =
        isCartReservationRecapStep ||
        currentStep === DiningStepId.RESERVATION_RECAP ||
        !hadReservationsOnEntry ||
        isAddReservationFlow;
      if (isRecapOrConfirm) {
        primaryLabel = alreadyPurchasedConfirmButtonLabel;
        processingPrimaryLabel = alreadyPurchasedUpdatingLabel;
        processingPrimaryIcon = alreadyPurchasedUpdatingIcon;
      } else {
        primaryLabel = alreadyPurchasedEditButtonLabel;
        processingPrimaryLabel = alreadyPurchasedUpdatingLabel;
        processingPrimaryIcon = alreadyPurchasedUpdatingIcon;
      }
    }

    // Processing source policy:
    // - footer-driven date/time flows are coupled with async slot confirmation (`isConfirming`)
    // - all other flows use generic drawer apply processing (`isProcessing`)
    const isPrimaryProcessing = isFooterDrivenDateTimeStep ? isConfirming : isProcessing;

    // Secondary CTA (Cancel) is intentionally constrained to edit contexts.
    const secondaryLabel =
      isCartReservationRecapStep ||
      isFooterDrivenDateTimeStep ||
      isCartOrAlreadyPurchasedPaxEditStep ||
      entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED
        ? closeDrawerCancelLabel
        : undefined;

    // Secondary CTA disable policy avoids conflicting actions while apply is in progress.
    // On cart recap we also keep cancel disabled while there are unsaved recap changes.
    const isSecondaryDisabled = () => {
      if (
        !hadReservationsOnEntry &&
        currentStep === DiningStepId.RESERVATION_RECAP &&
        entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED
      ) {
        return true;
      }
      if (isCartReservationRecapStep && (isPrimaryProcessing || hasCartReservationRecapChanges)) {
        return true;
      }
      if (
        (isFooterDrivenDateTimeStep || isCartOrAlreadyPurchasedPaxEditStep) &&
        isPrimaryProcessing
      ) {
        return true;
      }

      return false;
    };

    // Primary enablement matrix (updated):
    // - respects inline-only `PAX_SELECTION`
    // - implements PRODUCT / CART / ALREADY_PURCHASE flows per design doc
    const isPrimaryEnabled = (() => {
      // Inline CTA owns progression on passenger selection
      if (currentStep === DiningStepId.PAX_SELECTION) return false;

      // PRODUCT_CARD rules
      if (entryPoint === DiningFlowEntryPoint.PRODUCT_CARD) {
        if (currentStep === DiningStepId.RESERVATION_RECAP) return true;
        if (currentStep === DiningStepId.CHOOSE_WHEN_BOOK) {
          // TODO: If "make reservation later" mapping differs, adjust this condition.
          if (chooseWhenBookBannerVariant === ChooseWhenBookBannerVariant.GENERIC) return true;
          return reservationMode === DiningReservationMode.WITHOUT_TIMESLOT;
        }

        if (isPaxSelectionEditStep) {
          // Preserve inline-only behavior for PRODUCT_CARD
          return false;
        }

        if (currentStep === DiningStepId.DATE_TIME_SELECTION && !isFooterDrivenDateTimeStep)
          return false;
        if (isFooterDrivenDateTimeStep) return isFooterDrivenDateTimeApplyReady;
        if (currentStep === DiningStepId.DATE_SELECTION) return !!selectedDate && !!selectedTime;
        if (currentStep === DiningStepId.TIME_SELECTION) return !!selectedTime;

        return true;
      }

      // CART rules
      if (entryPoint === DiningFlowEntryPoint.CART) {
        // Recap step: enable on recap page (per requirements)
        if (currentStep === DiningStepId.CART_RESERVATION_RECAP)
          return hasCartReservationRecapChanges;

        // Pax-edit: behaviour depends on whether drawer had reservations on entry
        if (isPaxSelectionEditStep) {
          // If there were reservations when opening, footer primary must never enable
          if (hadReservationsOnEntry) return false;
          // Otherwise enable only when pax selection actually changed
          return Boolean(hasPaxSelectionChanges);
        }

        if (currentStep === DiningStepId.DATE_TIME_SELECTION && !isFooterDrivenDateTimeStep)
          return false;
        if (isFooterDrivenDateTimeStep) return isFooterDrivenDateTimeApplyReady;
        if (currentStep === DiningStepId.DATE_SELECTION) return !!selectedDate && !!selectedTime;

        if (currentStep === DiningStepId.TIME_SELECTION) {
          // If only one reservation slot is bookable, enable when a time is selected.
          if (maximumSlotsBookable === 1) return !!selectedTime;
          // If more than one slot, never enable in TIME selection for CART flows.
          return false;
        }

        if (currentStep === DiningStepId.CHOOSE_WHEN_BOOK) {
          if (chooseWhenBookBannerVariant === ChooseWhenBookBannerVariant.GENERIC) return true;
          return reservationMode === DiningReservationMode.WITHOUT_TIMESLOT;
        }

        if (currentStep === DiningStepId.RESERVATION_RECAP) return true;

        return true;
      }

      // ALREADY_PURCHASE rules
      if (entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED) {
        // Cart/recap: enable only when recap changes exist
        if (currentStep === DiningStepId.CART_RESERVATION_RECAP)
          return hasCartReservationRecapChanges;

        // If drawer opened with zero reservations, primary stays disabled until RESERVATION_RECAP
        if (hadReservationsOnEntry === false) {
          return currentStep === DiningStepId.RESERVATION_RECAP;
        }

        // If drawer opened with existing reservations, require user to select a different timeslot
        if (hadReservationsOnEntry) {
          // For DATE_TIME flows respect footer-driven behaviour
          if (currentStep === DiningStepId.DATE_TIME_SELECTION && !isFooterDrivenDateTimeStep)
            return false;
          if (isFooterDrivenDateTimeStep) return isFooterDrivenDateTimeApplyReady;

          if (currentStep === DiningStepId.RESERVATION_RECAP) return hasCartReservationRecapChanges;
        }

        return false;
      }

      // Fallback default (conservative)
      return true;
    })();

    return {
      primaryLabel,
      processingPrimaryLabel,
      processingPrimaryIcon,
      secondaryLabel,
      isSecondaryDisabled,
      isPrimaryProcessing,
      isPrimaryEnabled,
    };
  }, [
    addToCartButtonLabel,
    addingToCartButtonIcon,
    addingToCartButtonLabel,
    chooseWhenBookBannerVariant,
    closeDrawerCancelLabel,
    currentStep,
    entryPoint,
    hasCartReservationRecapChanges,
    isCartOrAlreadyPurchasedPaxEditStep,
    isCartReservationRecapStep,
    isConfirming,
    isFooterDrivenDateTimeApplyReady,
    isFooterDrivenDateTimeStep,
    isPaxSelectionEditStep,
    hasPaxSelectionChanges,
    isProcessing,
    maximumSlotsBookable,
    reservationMode,
    selectedDate,
    selectedTime,
    hadReservationsOnEntry,
    cartConfirmButtonLabel,
    cartEditButtonLabel,
    cartUpdatingLabel,
    cartUpdatingIcon,
    alreadyPurchasedConfirmButtonLabel,
    alreadyPurchasedEditButtonLabel,
    alreadyPurchasedUpdatingLabel,
    alreadyPurchasedUpdatingIcon,
  ]);
};
